import axiosConfig from './axiosConfig';
import { arcDashArray, arcMarkerPosition, SMALL_ARC_LEN } from '../components/DPR/onePageReport/arcGauge';
import { formatExpectedLength, sumProjectExpectedLength } from '../utils/sowExpectedLength';
import { readSowOdDisplay, readSowWtDisplay } from '../utils/dprSectionReportHeader';
import { getProjectDisplayFields, isOffshoreProject, formatProjectListDate } from '../utils/projectConfig';
import { readSowSectionId, sumExpectedJointsAcrossRecords } from '../utils/sowPipelineForm';

/** Peel API wrapper layers: { status, displayMessage, data: { … } }. */
export function unwrapOnePageApiPayload(raw) {
  let payload = raw;
  if (!payload || typeof payload !== 'object') return null;

  for (let depth = 0; depth < 5; depth += 1) {
    const inner = payload.data;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;

    const payloadHasContent =
      payload.dprLogId != null || payload.header != null || payload.bundle != null;
    if (payloadHasContent) break;

    const innerHasContent =
      inner.dprLogId != null || inner.header != null || inner.bundle != null;
    const innerIsStatusWrapper =
      (inner.status != null || inner.displayMessage != null) && !innerHasContent;

    if (innerHasContent) {
      payload = inner;
      break;
    }

    if (innerIsStatusWrapper || payload.status != null || payload.displayMessage != null) {
      payload = inner;
      continue;
    }

    break;
  }

  return payload;
}

function hasDashboardSections(payload) {
  if (!payload || typeof payload !== 'object') return false;
  return Boolean(
    payload.header
      || payload.progress?.pipeProgPct != null
      || payload.meta?.projectNumber
      || payload.bundle?.pipeProgPct != null
      || payload.bundle?.metaProjectNumber
      || payload.criticalConsumables?.hasData
      || (Array.isArray(payload.criticalConsumables?.leftGauges) && payload.criticalConsumables.leftGauges.length > 0)
      || payload.criticalConsumables?.rightWire
      || (Array.isArray(payload.bundle?.topConsumables) && payload.bundle.topConsumables.length > 0)
  );
}

function buildCriticalConsumablesFromTopConsumables(topConsumables) {
  if (!Array.isArray(topConsumables) || topConsumables.length === 0) return null;
  const leftGauges = topConsumables.map(normalizeConsumableGaugeItem);
  return {
    hasData: leftGauges.length > 0,
    leftGauges,
    rightWire: null,
  };
}

function buildCriticalConsumables(payload, bundle) {
  const candidates = [
    payload.criticalConsumables,
    bundle?.criticalConsumables,
  ].filter((item) => item && typeof item === 'object');

  const merged = candidates.find(
    (item) =>
      item.hasData === true
      || (Array.isArray(item.leftGauges) && item.leftGauges.length > 0)
      || item.rightWire
  ) ?? candidates[0];

  if (merged) {
    const leftGauges = (Array.isArray(merged.leftGauges) ? merged.leftGauges : [])
      .map(normalizeConsumableGaugeItem);

    let rightWire = merged.rightWire ?? null;
    if (rightWire && typeof rightWire === 'object') {
      rightWire = {
        ...rightWire,
        actualVsPlanned: rightWire.actualVsPlanned
          ? normalizeActualVsPlannedItem(rightWire.actualVsPlanned)
          : null,
        suppliedVsNeeded: rightWire.suppliedVsNeeded
          ? normalizeSuppliedVsNeededItem(rightWire.suppliedVsNeeded)
          : null,
      };
    }

    const hasData = Boolean(
      merged.hasData ?? (leftGauges.length > 0 || rightWire)
    );
    if (hasData) {
      return { hasData, leftGauges, rightWire };
    }
  }

  const topFallback = buildCriticalConsumablesFromTopConsumables(
    bundle?.topConsumables ?? payload?.topConsumables
  );
  if (topFallback) return topFallback;

  return { hasData: false, leftGauges: [], rightWire: null };
}

function pick(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (v != null && v !== '') return v;
  }
  return undefined;
}

/** Parse "Today 10" / "Today: 10" from PDF template `nums` field. */
function parseTodayFromNums(nums) {
  if (nums == null || nums === '') return undefined;
  const match = String(nums).trim().match(/today\s*:?\s*([\d.]+)/i);
  return match ? match[1] : undefined;
}

/** Parse numeric consumable values; strips thousands separators from backend strings. */
function parseConsumableNumber(val) {
  if (val == null || val === '') return NaN;
  if (typeof val === 'number') return Number.isFinite(val) ? val : NaN;
  const cleaned = String(val).replace(/,/g, '').trim();
  if (cleaned === '') return NaN;
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : NaN;
}

function formatConsumableDisplayNum(val) {
  if (val == null || val === '') return undefined;
  if (typeof val === 'number' && Number.isFinite(val)) {
    return val.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  const raw = String(val).trim();
  const num = parseConsumableNumber(raw);
  if (Number.isFinite(num)) {
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  return raw;
}

const AVAILABLE_NOTE_DEFAULT = 'Available = 100% + 20% Contingency';

function resolveConsumableUnit(item, isGas, gasUnits) {
  const fromItem = pick(item?.consumptionUnit, item?.unit);
  if (fromItem) return fromItem;
  if (isGas) {
    return gasUnits?.gasConsumptionUnitTitle || 'm³';
  }
  return 'bag';
}

function formatTodayUsageLine(item, todayVal, unit, isGas, gasSuffix) {
  if (item?.todayUsageLine) return item.todayUsageLine;
  const qty = todayVal != null && todayVal !== '' ? todayVal : '0';
  if (isGas) {
    return `Today usage: ${qty}${gasSuffix || ''}`;
  }
  const unitStr = String(unit || 'bag').trim();
  const qtyNum = parseConsumableNumber(qty);
  const isBag = /^bags?$/i.test(unitStr);
  const bagLabel = isBag
    ? (Number.isFinite(qtyNum) && qtyNum === 1 ? 'Bag' : 'Bags')
    : unitStr;
  return `Today usage: ${qty} ${bagLabel}`;
}

function withConsumableUsageFooter(item, {
  isGas = false,
  gasUnits = null,
  remainingLabelDefault = 'REMAINING',
  todayVal,
  /** Contingency note is welding-wire remaining stock only. */
  includeAvailableNote = false,
} = {}) {
  const unit = resolveConsumableUnit(item, isGas, gasUnits);
  const consumptionTotal = pick(
    item?.consumptionTotal,
    item?.totalQuantityUsed,
    item?.weekUsed,
    item?.week,
    item?.weeklyUsed,
    '0'
  );
  return {
    remainingLabel: pick(item?.remainingLabel, remainingLabelDefault),
    availableNote: includeAvailableNote
      ? pick(item?.availableNote, AVAILABLE_NOTE_DEFAULT)
      : undefined,
    consumptionTotal: consumptionTotal != null ? String(consumptionTotal) : '0',
    consumptionUnit: unit,
    todayUsageLine: formatTodayUsageLine(
      item,
      todayVal,
      unit,
      isGas,
      gasUnits?.gasConsumptionUnitSuffix
    ),
  };
}

function resolveWeeklyLimitVal(item, weeklyLimit, limitNum) {
  return pick(
    item.weeklyLimitVal,
    formatConsumableDisplayNum(weeklyLimit),
    Number.isFinite(limitNum) ? formatConsumableDisplayNum(limitNum) : undefined
  );
}

/** Gas / 80-20 unit labels from API or title, e.g. CONSUMPTION (M³) / " m³". */
export function resolveGasConsumptionUnits(item = {}, title = '') {
  const titleStr = String(title || item.title || item.name || '');
  const fromApiTitle = pick(
    item.gasConsumptionUnitTitle,
    item.gasUnitTitle,
    item.consumptionUnitTitle
  );
  const fromApiSuffix = pick(
    item.gasConsumptionUnitSuffix,
    item.gasUnitSuffix,
    item.consumptionUnitSuffix,
    item.unitSuffix
  );

  const parenMatch = titleStr.match(/\(([^)]+)\)\s*$/);
  const inferred = parenMatch ? parenMatch[1].trim() : null;

  let unitTitle = fromApiTitle || inferred || 'BAR';
  unitTitle = String(unitTitle).trim();
  if (/^m3$/i.test(unitTitle)) unitTitle = 'M³';

  let suffix = fromApiSuffix;
  if (suffix == null || String(suffix).trim() === '') {
    if (/m³|m3/i.test(unitTitle)) suffix = ' m³';
    else if (/bar/i.test(unitTitle)) suffix = ' Bar';
    else suffix = ` ${unitTitle}`;
  } else {
    suffix = String(suffix);
    if (!suffix.startsWith(' ')) suffix = ` ${suffix}`;
  }

  return {
    gasConsumptionUnitTitle: unitTitle,
    gasConsumptionUnitSuffix: suffix,
  };
}

function isGasConsumableItem(item, titleStr) {
  return Boolean(
    item?.isGas
    || item?.gaugeMode === 'remainingStock'
    || titleStr.includes('80/20')
    || titleStr.includes('0/20')
    || (titleStr.includes('CONSUMPTION') && (
      titleStr.includes('BAR')
      || titleStr.includes('M³')
      || titleStr.includes('M3')
    ))
  );
}

function hasBackendGaugeGeometry(item) {
  return item?.consUsedArcDash != null
    && item?.barColorUsed != null
    && pick(item.gaugeHeroVal, item.remainingStock, item.supplied) != null;
}

/**
 * Normalize left-column weekly-limit consumable gauge (matches PDF Handlebars cons-mod).
 * Gas / 80-20 CONSUMPTION: arc = remaining stock vs scaleMax; green above weekly limit, red below.
 */
export function normalizeConsumableGaugeItem(item) {
  if (!item || typeof item !== 'object') return item;

  const title = pick(item.title, item.name);
  const titleStr = String(title || '').toUpperCase();
  const consumptionToday = pick(
    item.consumptionToday,
    item.todayUsed,
    item.today,
    item.usedToday,
    parseTodayFromNums(item.nums)
  );
  const weeklyLimit = pick(item.weeklyLimitQty, item.weekLimit, item.weeklyLimit);
  const limitNum = parseConsumableNumber(weeklyLimit);
  const scaleMaxRaw = pick(item.scaleMax, item.gaugeScaleMax, item.suppliedVal);
  const weeklyLimitVal = resolveWeeklyLimitVal(item, weeklyLimit, limitNum);

  const isGas = isGasConsumableItem(item, titleStr);
  const gasUnits = isGas ? resolveGasConsumptionUnits(item, title) : null;

  if (isGas) {
    const heroRaw = pick(item.gaugeHeroVal, item.remainingStock, item.stockRemaining, item.remaining);
    const remaining = parseConsumableNumber(heroRaw);
    let scaleMaxNum = parseConsumableNumber(scaleMaxRaw);
    if (!Number.isFinite(scaleMaxNum)) {
      if (Number.isFinite(limitNum) && limitNum > 0) {
        scaleMaxNum = limitNum * 2;
      } else if (Number.isFinite(remaining) && remaining > 0) {
        scaleMaxNum = Math.ceil(remaining * 1.1);
      } else {
        scaleMaxNum = 30;
      }
    }

    const heroDisplay = pick(
      heroRaw != null && heroRaw !== '' ? heroRaw : undefined,
      Number.isFinite(remaining) ? formatConsumableDisplayNum(remaining) : undefined,
      '0'
    );

    if (hasBackendGaugeGeometry(item)) {
      return {
        ...item,
        title,
        name: pick(item.name, title),
        isGas: true,
        ...gasUnits,
        consumptionToday,
        gaugeHeroVal: heroDisplay,
        weeklyLimitQty: weeklyLimit ?? item.weeklyLimitQty,
        weeklyLimitVal,
        scaleMax: pick(item.scaleMax, scaleMaxNum),
        showWeeklyLimitLbl: item.showWeeklyLimitLbl !== false,
        showLimitMarker: item.showLimitMarker !== false && item.limitMarkerX != null,
        nums: item.nums ?? (consumptionToday != null ? `Today ${consumptionToday}` : undefined),
        ...withConsumableUsageFooter(item, {
          isGas: true,
          gasUnits,
          remainingLabelDefault: 'REMAINING',
          todayVal: consumptionToday,
        }),
      };
    }

    let arcPct = parseConsumableNumber(item.gaugePct ?? item.percent);
    if (!Number.isFinite(arcPct)) {
      arcPct = scaleMaxNum > 0 && Number.isFinite(remaining)
        ? (remaining / scaleMaxNum) * 100
        : 0;
    }

    const arc = arcDashArray(arcPct, SMALL_ARC_LEN);

    const aboveLimit = Number.isFinite(remaining) && Number.isFinite(limitNum) && remaining >= limitNum;
    const barColorUsed = item.barColorUsed ?? (aboveLimit ? '#22c55e' : '#dc2626');
    const badgeClass = item.badgeClass ?? (aboveLimit ? 'ok' : 'low');

    let limitMarkerX = item.limitMarkerX;
    let limitMarkerY = item.limitMarkerY;
    const showLimitMarker = item.showLimitMarker !== false
      && (Number.isFinite(limitNum) || limitMarkerX != null);
    if (showLimitMarker && limitMarkerX == null && Number.isFinite(limitNum) && scaleMaxNum > 0) {
      const pos = arcMarkerPosition((limitNum / scaleMaxNum) * 100);
      limitMarkerX = pos.x;
      limitMarkerY = pos.y;
    }

    return {
      ...item,
      title,
      name: pick(item.name, title),
      isGas: true,
      ...gasUnits,
      consumptionToday,
      gaugeHeroVal: heroDisplay,
      gaugePct: Number.isFinite(arcPct) ? arcPct.toFixed(2) : '0',
      weeklyLimitQty: weeklyLimit ?? item.weeklyLimitQty,
      weeklyLimitVal,
      scaleMax: scaleMaxNum,
      consUsedArcDash: item.consUsedArcDash ?? arc.dash,
      consArcLen: item.consArcLen ?? arc.len,
      barColorUsed,
      badgeClass,
      badge: item.badge ?? (aboveLimit ? 'Above wk lim' : 'Below wk lim'),
      showWeeklyLimitLbl: item.showWeeklyLimitLbl !== false,
      showLimitMarker: Boolean(showLimitMarker && limitMarkerX != null),
      limitMarkerX,
      limitMarkerY,
      nums: item.nums ?? (consumptionToday != null ? `Today ${consumptionToday}` : undefined),
      ...withConsumableUsageFooter(item, {
        isGas: true,
        gasUnits,
        remainingLabelDefault: 'REMAINING',
        todayVal: consumptionToday,
      }),
    };
  }

  if (hasBackendGaugeGeometry(item)) {
    const heroRaw = pick(item.gaugeHeroVal, item.weekUsed, item.week, item.weeklyUsed);
    return {
      ...item,
      title,
      name: pick(item.name, title),
      consumptionToday,
      gaugeHeroVal: pick(
        heroRaw != null && heroRaw !== '' ? heroRaw : undefined,
        formatConsumableDisplayNum(parseConsumableNumber(heroRaw)),
        '0'
      ),
      weeklyLimitQty: weeklyLimit ?? item.weeklyLimitQty,
      weeklyLimitVal,
      scaleMax: pick(item.scaleMax, scaleMaxRaw),
      showWeeklyLimitLbl: item.showWeeklyLimitLbl !== false,
      isGas: false,
      nums: item.nums ?? (consumptionToday != null ? `Today ${consumptionToday}` : undefined),
      ...withConsumableUsageFooter(item, {
        isGas: false,
        remainingLabelDefault: 'REMAINING',
        todayVal: consumptionToday,
      }),
    };
  }

  const weekUsed = pick(item.weekUsed, item.week, item.weeklyUsed);
  const heroNum = parseConsumableNumber(pick(item.gaugeHeroVal, weekUsed));
  const scaleMaxNum = Number.isFinite(parseConsumableNumber(scaleMaxRaw))
    ? parseConsumableNumber(scaleMaxRaw)
    : Number.isFinite(limitNum) && limitNum > 0
      ? limitNum
      : Number.isFinite(heroNum) && heroNum > 0
        ? Math.ceil(heroNum * 2)
        : 30;

  let arcPct = parseConsumableNumber(item.gaugePct ?? item.percent);
  if (!Number.isFinite(arcPct)) {
    if (Number.isFinite(heroNum) && Number.isFinite(limitNum) && limitNum > 0) {
      arcPct = (heroNum / limitNum) * 100;
    } else if (Number.isFinite(heroNum) && scaleMaxNum > 0) {
      arcPct = (heroNum / scaleMaxNum) * 100;
    } else {
      arcPct = 0;
    }
  }

  const arc = arcDashArray(arcPct, SMALL_ARC_LEN);

  const showLimitMarker = item.showLimitMarker !== false
    && (Number.isFinite(limitNum) || item.limitMarkerX != null);
  let limitMarkerX = item.limitMarkerX;
  let limitMarkerY = item.limitMarkerY;
  if (showLimitMarker && limitMarkerX == null && Number.isFinite(limitNum) && scaleMaxNum > 0) {
    const limitPct = (limitNum / scaleMaxNum) * 100;
    const pos = arcMarkerPosition(limitPct);
    limitMarkerX = pos.x;
    limitMarkerY = pos.y;
  }

  const badgeClass = item.badgeClass ?? 'ok';
  const barColorByBadge = badgeClass === 'warn'
    ? '#f59e0b'
    : badgeClass === 'low'
      ? '#dc2626'
      : '#22c55e';

  return {
    ...item,
    title,
    name: pick(item.name, title),
    consumptionToday,
    gaugeHeroVal: pick(
      item.gaugeHeroVal,
      weekUsed,
      Number.isFinite(heroNum) ? formatConsumableDisplayNum(heroNum) : 0
    ),
    gaugePct: Number.isFinite(arcPct) ? arcPct.toFixed(2) : '0',
    weeklyLimitQty: weeklyLimit ?? item.weeklyLimitQty,
    weeklyLimitVal,
    scaleMax: scaleMaxNum,
    consUsedArcDash: item.consUsedArcDash ?? arc.dash,
    consArcLen: item.consArcLen ?? arc.len,
    barColorUsed: item.barColorUsed ?? barColorByBadge,
    badgeClass,
    showWeeklyLimitLbl: item.showWeeklyLimitLbl !== false,
    showLimitMarker: Boolean(showLimitMarker && limitMarkerX != null),
    limitMarkerX,
    limitMarkerY,
    isGas: false,
    nums: item.nums ?? (consumptionToday != null ? `Today ${consumptionToday}` : undefined),
  };
}

/** Wire ACT vs PLAN block — respects backend showBadge (IPT hides over-plan badge). */
export function normalizeActualVsPlannedItem(item) {
  if (!item || typeof item !== 'object') return item;

  const actualPct = pick(item.actualPct, item.actualPercent, '0');
  const plannedPct = pick(item.plannedPct, item.plannedPercent, '100');
  const actualNum = parseFloat(actualPct);
  const plannedNum = parseFloat(plannedPct);

  let showBadge = item.showBadge;
  if (showBadge === undefined || showBadge === null) {
    const overPlan = Number.isFinite(actualNum) && Number.isFinite(plannedNum) && actualNum > plannedNum;
    showBadge = Boolean(item.badge) && !overPlan;
  } else {
    showBadge = Boolean(showBadge);
  }

  return {
    ...item,
    actualPct,
    plannedPct,
    actualBarWidthPct: pick(item.actualBarWidthPct, actualPct) ?? '0',
    plannedBarWidthPct: pick(item.plannedBarWidthPct, plannedPct) ?? '100',
    showBadge,
  };
}

/** Normalize PIH wire "Supplied vs Needed" semicircle (110 = 100% marker). */
export function normalizeSuppliedVsNeededItem(item) {
  if (!item || typeof item !== 'object') return item;

  const needQty = parseConsumableNumber(pick(item.needQty, item.requiredQty, item.needMarkerValue) ?? 110);
  const scaleMaxNum = parseConsumableNumber(
    pick(item.scaleMax, item.gaugeScaleMax, Number.isFinite(needQty) ? needQty * 1.2 : 132)
  );
  const heroNum = parseConsumableNumber(pick(item.gaugeHeroVal, item.supplied, item.value) ?? 0);

  let arcPct = parseConsumableNumber(item.gaugePct ?? item.percent);
  if (!Number.isFinite(arcPct) && scaleMaxNum > 0 && Number.isFinite(heroNum)) {
    arcPct = (heroNum / scaleMaxNum) * 100;
  }
  if (!Number.isFinite(arcPct)) arcPct = 0;

  if (item.consUsedArcDash != null && item.barColorUsed != null && item.gaugeHeroVal != null) {
    return {
      ...item,
      gaugeHeroVal: pick(item.gaugeHeroVal, item.supplied, item.value, item.remainingStock, item.remaining),
      scaleMax: pick(item.scaleMax, scaleMaxNum),
      remainingLabel: pick(item.remainingLabel, 'REMAINING STOCK'),
      ...withConsumableUsageFooter(item, {
        remainingLabelDefault: 'REMAINING STOCK',
        includeAvailableNote: true,
        todayVal: pick(item.consumptionToday, item.todayUsed, item.today, item.usedToday),
      }),
    };
  }

  const needPct = scaleMaxNum > 0 && Number.isFinite(needQty) ? (needQty / scaleMaxNum) * 100 : 83.33;
  const needPos = item.needMarkerX != null
    ? { x: item.needMarkerX, y: item.needMarkerY }
    : arcMarkerPosition(needPct);

  const remainingHero = pick(item.gaugeHeroVal, item.remainingStock, item.remaining, item.supplied, item.value) ?? heroNum;
  const remainingForArc = parseConsumableNumber(remainingHero);
  let remainingArcPct = arcPct;
  if (!item.gaugePct && !item.percent && Number.isFinite(remainingForArc) && scaleMaxNum > 0) {
    remainingArcPct = (remainingForArc / scaleMaxNum) * 100;
  }
  const remainingArc = arcDashArray(remainingArcPct, SMALL_ARC_LEN);

  return {
    ...item,
    gaugeHeroVal: remainingHero,
    gaugePct: remainingArcPct.toFixed(2),
    scaleMax: scaleMaxNum,
    consUsedArcDash: remainingArc.dash,
    consArcLen: remainingArc.len,
    barColorUsed: item.barColorUsed ?? '#087e8b',
    requiredLabel: pick(item.availableNote, AVAILABLE_NOTE_DEFAULT, item.requiredLabel),
    remainingLabel: pick(item.remainingLabel, 'REMAINING STOCK'),
    showNeedMarker: item.showNeedMarker !== false,
    needMarkerX: needPos.x,
    needMarkerY: needPos.y,
    ...withConsumableUsageFooter(item, {
      remainingLabelDefault: 'REMAINING STOCK',
      includeAvailableNote: true,
      todayVal: pick(item.consumptionToday, item.todayUsed, item.today, item.usedToday),
    }),
  };
}

function buildHeader(payload, bundle) {
  if (payload.header && typeof payload.header === 'object') return payload.header;
  if (!bundle || typeof bundle !== 'object') return null;
  return {
    bundleTitle: bundle.bundleTitle,
    bundleReportNum: bundle.bundleReportNum,
    clientTag: bundle.clientTag,
    // Kept for backward compat if BE still sends logos; UI renders text tag only.
    clientLogoSrc: bundle.clientLogoSrc,
    crceLogoSrc: bundle.crceLogoSrc,
  };
}

function buildMeta(payload, bundle) {
  const meta = payload.meta && typeof payload.meta === 'object' ? payload.meta : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  const header = payload.header && typeof payload.header === 'object' ? payload.header : {};
  // Prefer pipeline-level length when project-level blank (avoid blank chip + duplicate labels)
  const expectedProjectLength = pick(
    meta.expectedProjectLength,
    meta.expectedProjectLengthM,
    b.metaExpectedProjectLength,
    header.expectedProjectLength
  );
  const expectedPipeLength = pick(
    meta.expectedPipeLength,
    meta.expectedPipeLengthM,
    b.metaExpectedPipeLength,
    header.expectedPipeLength,
    header.length
  );
  return {
    projectNumber: pick(
      meta.projectNumber,
      b.metaProjectNumber,
      header.projectNumber,
      header.projectId,
      payload.projectNumber,
      payload.projectId
    ),
    client: pick(
      meta.client,
      b.metaClient,
      header.client,
      header.clientName,
      header.clientTag,
      b.clientTag,
      payload.clientName,
      payload.contractor
    ),
    finalClient: pick(meta.finalClient, b.metaFinalClient, header.finalClient),
    vessel: pick(meta.vessel, b.metaVessel, header.vessel, header.siteLocationDetail),
    date: pick(meta.date, b.metaDate, header.date, payload.logDate),
    projectStart: pick(meta.projectStart, b.metaProjectStart, header.projectStart),
    expectedProjectLength,
    expectedProjectWelds: pick(meta.expectedProjectWelds, b.metaExpectedProjectWelds, header.expectedProjectWelds),
    crceRepresentative: pick(
      meta.crceRepresentative,
      b.metaCrceRepresentative,
      header.crceRepresentative
    ),
    manager: pick(meta.manager, b.metaManager, header.manager),
    showProjectStatusCell: Boolean(
      meta.showProjectStatusCell ?? b.showProjectStatusCell ?? header.showProjectStatusCell
    ),
    ongoingPipe: pick(meta.ongoingPipe, b.metaOngoingPipe, header.sectionLabel, header.mainline, header.pipeNumber),
    od: pick(
      meta.od,
      b.metaOD,
      header.od,
      header.outerDiameterInch,
      header.outer_diameter_inch,
      header.odInches
    ),
    wt: pick(
      meta.wt,
      b.metaWT,
      header.wt,
      header.wallThicknessMm,
      header.wall_thickness_mm
    ),
    location: pick(meta.location, b.metaLocation, header.location, header.country),
    pipeStart: pick(meta.pipeStart, b.metaPipeStart, header.pipeStart),
    expectedPipeLength,
    expectedPipeWelds: pick(meta.expectedPipeWelds, b.metaExpectedPipeWelds, header.expectedPipeWelds),
    ndtType: pick(meta.ndtType, b.metaNdtType, header.ndtType),
    dprProjectStatus: pick(meta.dprProjectStatus, b.metaDprProjectStatus, header.dprProjectStatus),
    suppressPipelineProgress: Boolean(
      pick(
        meta.suppressPipelineProgress,
        b.suppressPipelineProgress,
        header.suppressPipelineProgress,
        payload.suppressPipelineProgress
      )
    ),
  };
}

function buildProgress(payload, bundle) {
  const p = payload.progress && typeof payload.progress === 'object' ? payload.progress : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  return {
    pipeProgPct: pick(p.pipeProgPct, b.pipeProgPct),
    pipeArcDash: pick(p.pipeArcDash, b.pipeArcDash),
    pipeArcLen: pick(p.pipeArcLen, b.pipeArcLen),
    projProgPct: pick(p.projProgPct, b.projProgPct),
    projArcDash: pick(p.projArcDash, b.projArcDash),
    projArcLen: pick(p.projArcLen, b.projArcLen),
    progNdtAcceptedSowToDate: pick(p.progNdtAcceptedSowToDate, b.progNdtAcceptedSowToDate),
    progNdtAcceptedProjectToDate: pick(p.progNdtAcceptedProjectToDate, b.progNdtAcceptedProjectToDate),
    progressPipeFootCaption: pick(p.progressPipeFootCaption, b.progressPipeFootCaption),
    progressProjectFootCaption: pick(p.progressProjectFootCaption, b.progressProjectFootCaption),
    ndtAccBoxLabel: pick(p.ndtAccBoxLabel, b.ndtAccBoxLabel),
    progTodayTitle: pick(p.progTodayTitle, b.progTodayTitle),
    progTodayStn1: pick(p.progTodayStn1, b.progTodayStn1),
    progTodayStn1Label: pick(p.progTodayStn1Label, b.progTodayStn1Label),
    progTodayNdtCompleted: pick(p.progTodayNdtCompleted, b.progTodayNdtCompleted),
    progTodayNdtCompletedLabel: pick(p.progTodayNdtCompletedLabel, b.progTodayNdtCompletedLabel),
    progTodayNdtAccepted: pick(p.progTodayNdtAccepted, b.progTodayNdtAccepted),
    progTodayNdtAcceptedLabel: pick(p.progTodayNdtAcceptedLabel, b.progTodayNdtAcceptedLabel),
    totalJointsSt1: pick(p.totalJointsSt1, b.totalJointsSt1),
    totalJointsAccepted: pick(p.totalJointsAccepted, b.totalJointsAccepted),
    totalReweldsSt1: pick(p.totalReweldsSt1, b.totalReweldsSt1),
    totalNdtRepair: pick(p.totalNdtRepair, b.totalNdtRepair),
    totalNdtCutout: pick(p.totalNdtCutout, b.totalNdtCutout),
    ndtRejectPctPipe: pick(p.ndtRejectPctPipe, b.ndtRejectPctPipe),
    ndtRejectPctProject: pick(p.ndtRejectPctProject, b.ndtRejectPctProject),
  };
}

function buildQuality(payload, bundle) {
  const q = payload.quality && typeof payload.quality === 'object' ? payload.quality : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  const pipeLabel = pick(q.pipeDefectGaugeLabel, b.pipeDefectGaugeLabel);
  const projectLabel = pick(q.projectDefectGaugeLabel, b.projectDefectGaugeLabel);
  const isBlankLabel = (v) => {
    if (v == null) return true;
    const s = String(v).trim();
    return !s || s === '—' || s === '-';
  };
  return {
    weldLogRows: Array.isArray(q.weldLogRows) ? q.weldLogRows : (Array.isArray(b.weldLogRows) ? b.weldLogRows : []),
    hasWeldLogRows: Boolean(q.hasWeldLogRows ?? b.hasWeldLogRows),
    weldLogTruncated: Boolean(q.weldLogTruncated ?? b.weldLogTruncated),
    qualCutOutStn1: pick(q.qualCutOutStn1, b.qualCutOutStn1),
    qualNdtRepair: pick(q.qualNdtRepair, b.qualNdtRepair),
    qualRepairLabel: pick(q.qualRepairLabel, b.qualRepairLabel),
    qualNdtCutOut: pick(q.qualNdtCutOut, b.qualNdtCutOut),
    pipeDefPct: pick(q.pipeDefPct, b.pipeDefPct),
    pipeDefArcDash: pick(q.pipeDefArcDash, b.pipeDefArcDash),
    pipeDefGaugeColor: pick(q.pipeDefGaugeColor, b.pipeDefGaugeColor),
    projDefPct: pick(q.projDefPct, b.projDefPct),
    projDefArcDash: pick(q.projDefArcDash, b.projDefArcDash),
    projDefGaugeColor: pick(q.projDefGaugeColor, b.projDefGaugeColor),
    // Match PDF Handlebars defaults when API/bundle omit or send dash placeholders.
    pipeDefectGaugeLabel: isBlankLabel(pipeLabel) ? '% NDT Rejects ongoing pipeline' : pipeLabel,
    projectDefectGaugeLabel: isBlankLabel(projectLabel)
      ? '% NDT Rejects overall project'
      : projectLabel,
    pipeDefectGaugeCaption: (() => {
      const raw = pick(q.pipeDefectGaugeCaption, b.pipeDefectGaugeCaption);
      // Always use reject-joints footer for ongoing pipe (match overview copy).
      if (isBlankLabel(raw) || /accepted/i.test(String(raw)) || /rejects ongoing/i.test(String(raw))) {
        return 'Based on NDT Reject joints (ongoing pipeline)';
      }
      return raw;
    })(),
    projectDefectGaugeCaptionLine1: (() => {
      const raw = pick(q.projectDefectGaugeCaptionLine1, b.projectDefectGaugeCaptionLine1);
      if (isBlankLabel(raw) || (/rejects/i.test(String(raw)) && !/based on/i.test(String(raw)))) {
        return 'Based on NDT Reject joints (overall project)';
      }
      if (/accepted/i.test(String(raw))) {
        return 'Based on NDT Reject joints (overall project)';
      }
      return raw;
    })(),
    // Do not repeat the gauge heading under the overall-project gauge.
    projectDefectGaugeCaptionLine2: (() => {
      const raw = pick(q.projectDefectGaugeCaptionLine2, b.projectDefectGaugeCaptionLine2);
      if (isBlankLabel(raw) || /rejects overall/i.test(String(raw))) return '';
      return raw;
    })(),
    ndtRejectCapLabel: pick(q.ndtRejectCapLabel, b.ndtRejectCapLabel),
    ndtQualityNote: pick(q.ndtQualityNote, b.ndtQualityNote),
    rejectPctFormulaNote: pick(q.rejectPctFormulaNote, b.rejectPctFormulaNote),
  };
}

function buildSafety(payload, bundle) {
  const s = payload.safety && typeof payload.safety === 'object' ? payload.safety : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  const tbtToday = pick(
    s.tbtToday,
    s.tbtTodayCount,
    s.toolboxTalkKpi?.today,
    b.safetyTbtTodayCount,
    b.safetyTbtToday
  );
  const tbtToDate = pick(
    s.tbtToDate,
    s.toolboxTalkKpi?.toDate,
    b.safetyTbtToDate
  );
  const tbtComments = normalizeToolboxTalkComments(pick(
    s.tbtComments,
    s.toolboxTalkComments,
    s.toolboxTalkCommentsKpi,
    b.safetyTbtComments
  ));
  return {
    incToday: pick(s.incToday, b.safetyIncToday),
    incToDate: pick(s.incToDate, b.safetyIncToDate),
    ltiToday: pick(s.ltiToday, b.safetyLtiToday),
    ltiToDate: pick(s.ltiToDate, b.safetyLtiToDate),
    tbtToday,
    tbtToDate,
    tbtComments,
  };
}

/** Backend sometimes sends a comments object; UI needs a plain string (blank if empty). */
export function normalizeToolboxTalkComments(value) {
  if (value == null || value === '' || value === '—' || value === '-') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeToolboxTalkComments(item))
      .filter(Boolean)
      .join('\n')
      .trim();
  }
  if (typeof value === 'object') {
    const parts = [
      value.text,
      value.comment,
      value.comments,
      value.message,
      value.day,
      value.night,
      value.dayShift,
      value.nightShift,
      value.general,
    ]
      .map((part) => normalizeToolboxTalkComments(part))
      .filter(Boolean);
    return parts.join('\n').trim();
  }
  return '';
}

function buildDowntime(payload, bundle) {
  const d = payload.downtime && typeof payload.downtime === 'object' ? payload.downtime : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  const rows = Array.isArray(d.rows)
    ? d.rows
    : (Array.isArray(b.dtRows) ? b.dtRows : []);
  return {
    rows,
    columnTotals: d.columnTotals ?? b.dtColumnTotals ?? {},
    projectToDateMin: pick(d.projectToDateMin, b.dtProjectToDateMin),
  };
}

function buildManpower(payload, bundle) {
  const m = payload.manpower && typeof payload.manpower === 'object' ? payload.manpower : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  return {
    pobDay: pick(m.pobDay, b.pobDay),
    pobNight: pick(m.pobNight, b.pobNight),
    pobSixSix: pick(m.pobSixSix, b.pobSixSix),
    pobEightEight: pick(m.pobEightEight, b.pobEightEight),
    showOffshorePobShiftGrid: Boolean(
      m.showOffshorePobShiftGrid ?? b.showOffshorePobShiftGrid
    ),
    manHoursToDate: pick(
      m.manHoursToDate,
      m.toDateManHours,
      m.manpowerToDate,
      b.manHoursToDate,
      b.toDateManHours,
      b.manpowerToDate
    ),
  };
}

function buildComments(payload, bundle) {
  const c = payload.comments && typeof payload.comments === 'object' ? payload.comments : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  return {
    generalDay: pick(c.generalDay, b.generalCommentsDay, b.generalComments),
    generalNight: pick(c.generalNight, b.generalCommentsNight),
    generalMerged: pick(c.generalMerged, b.generalComments),
  };
}

function buildSignatures(payload, bundle) {
  if (payload.signatures && typeof payload.signatures === 'object') return payload.signatures;
  if (!bundle || typeof bundle !== 'object') return null;
  return {
    noteManager: bundle.sigNoteManager,
    noteClient: bundle.sigNoteClient,
    noteApprover: bundle.sigNoteApprover,
    approvedBy1: {
      label: bundle.approvedBy1Label,
      name: bundle.approvedBy1Name,
      position: bundle.approvedBy1Position,
      signature: bundle.approvedBy1Signature,
    },
    approvedBy2: {
      label: bundle.approvedBy2Label,
      name: bundle.approvedBy2Name,
      position: bundle.approvedBy2Position,
      signature: bundle.approvedBy2Signature,
    },
    approvedBy3: {
      label: bundle.approvedBy3Label,
      name: bundle.approvedBy3Name,
      position: bundle.approvedBy3Position,
      signature: bundle.approvedBy3Signature,
    },
  };
}

/**
 * Fill blank one-page meta fields from selected project / pipeline (client fallback).
 */
export function enrichMetaFromProjectContext(meta, { project, pipeline, dprLog, allPipelines, date } = {}) {
  if (!meta || typeof meta !== 'object') return meta || {};
  const next = { ...meta };
  const fill = (key, ...vals) => {
    if (next[key] != null && String(next[key]).trim() !== '') return;
    for (let i = 0; i < vals.length; i += 1) {
      const v = vals[i];
      if (v == null || v === '' || v === '—') continue;
      next[key] = v;
      return;
    }
  };
  const fillNumeric = (key, ...vals) => {
    // Keep explicit 0 / "0" from the dashboard API (do not treat as blank).
    if (next[key] != null && String(next[key]).trim() !== '') return;
    for (let i = 0; i < vals.length; i += 1) {
      const v = vals[i];
      if (v == null || v === '') continue;
      next[key] = v;
      return;
    }
  };

  const pipelines =
    Array.isArray(allPipelines) && allPipelines.length > 0
      ? allPipelines
      : pipeline
        ? [pipeline]
        : [];

  const projectLengthTotal = sumProjectExpectedLength(pipelines);
  const projectJointsTotal = sumExpectedJointsAcrossRecords(pipelines);
  const od = pipeline ? readSowOdDisplay(pipeline) : '—';
  const wt = pipeline ? readSowWtDisplay(pipeline) : '—';

  const display = project ? getProjectDisplayFields(project) : {};
  const offshore = isOffshoreProject(project);
  const logDate = dprLog?.logDate ?? dprLog?.date ?? dprLog?.reportDate;

  fill('projectNumber', project?.projectId, project?.projectNumber, project?.pihRefId);
  fill('client', project?.clientName, project?.contractor);
  fill('finalClient', project?.finalClient);
  fill('date', formatProjectListDate(logDate, { empty: null }), formatProjectListDate(date, { empty: null }));
  fill(
    'projectStart',
    formatProjectListDate(project?.startDate, { empty: null }),
    formatProjectListDate(project?.start_date, { empty: null })
  );
  fill('crceRepresentative', display.crceRepresentative);
  if (offshore) {
    fill('manager', project?.manager, display.crceManager);
    if (display.siteLocationDetail) {
      next.vessel = display.siteLocationDetail;
    } else {
      fill('vessel', project?.siteLocationDetail);
    }
  } else {
    fill('manager', project?.manager, display.crceRepresentative);
    fill('vessel', display.siteLocationDetail, project?.siteLocationDetail);
  }
  fill('location', project?.location);
  fill('od', od !== '—' ? od : null);
  fill('wt', wt !== '—' ? wt : null);
  fillNumeric(
    'expectedProjectLength',
    projectLengthTotal != null ? formatExpectedLength(projectLengthTotal) : null
  );
  fillNumeric('expectedProjectWelds', projectJointsTotal);
  // Pipe expected welds/length come from the one-page dashboard API only
  // (meta.expectedPipeWelds / bundle.metaExpectedPipeWelds) — never SOW plannedWelds.
  if (pipeline && !next.suppressPipelineProgress) {
    fill(
      'ongoingPipe',
      pipeline.pipelineSection,
      pipeline.pipeStringId,
      readSowSectionId(pipeline)
    );
  }
  fill('ndtType', project?.ndtType, Array.isArray(project?.ndtType) ? project.ndtType.join(', ') : null);
  fill('dprProjectStatus', dprLog?.dprProjectStatus);
  return applySuppressPipelineProgress(next);
}

/** When BE suppresses pipeline progress (e.g. non-Production): blank pipe, dash for welds/length. */
export function applySuppressPipelineProgress(meta) {
  if (!meta || typeof meta !== 'object' || !meta.suppressPipelineProgress) return meta || {};
  return {
    ...meta,
    expectedPipeWelds: '',
    expectedPipeLength: '',
    ongoingPipe: '',
  };
}

/** Fill blank HTML-preview bundle keys from project / pipeline. */
export function enrichBundleFromProjectContext(bundle, ctx = {}) {
  if (!bundle || typeof bundle !== 'object') return bundle || {};
  const meta = enrichMetaFromProjectContext(
    {
      projectNumber: bundle.metaProjectNumber,
      client: bundle.metaClient,
      finalClient: bundle.metaFinalClient,
      vessel: bundle.metaVessel,
      od: bundle.metaOD,
      wt: bundle.metaWT,
      expectedProjectLength: bundle.metaExpectedProjectLength,
      expectedPipeLength: bundle.metaExpectedPipeLength,
      expectedPipeWelds: bundle.metaExpectedPipeWelds,
      ongoingPipe: bundle.metaOngoingPipe,
      location: bundle.metaLocation,
      manager: bundle.metaManager,
      crceRepresentative: bundle.metaCrceRepresentative,
      showProjectStatusCell: bundle.showProjectStatusCell,
      date: bundle.metaDate,
      projectStart: bundle.metaProjectStart,
      ndtType: bundle.metaNdtType,
      dprProjectStatus: bundle.metaProjectStatus,
      suppressPipelineProgress: bundle.suppressPipelineProgress,
    },
    ctx
  );
  return {
    ...bundle,
    metaProjectNumber: meta.projectNumber ?? bundle.metaProjectNumber,
    metaClient: meta.client ?? bundle.metaClient,
    metaFinalClient: meta.finalClient ?? bundle.metaFinalClient,
    metaVessel: meta.vessel ?? bundle.metaVessel,
    metaDate: meta.date ?? bundle.metaDate,
    metaProjectStart: meta.projectStart ?? bundle.metaProjectStart,
    metaOD: meta.od ?? bundle.metaOD,
    metaWT: meta.wt ?? bundle.metaWT,
    metaExpectedProjectLength: meta.expectedProjectLength ?? bundle.metaExpectedProjectLength,
    metaExpectedPipeLength: meta.expectedPipeLength ?? bundle.metaExpectedPipeLength,
    metaExpectedPipeWelds: meta.expectedPipeWelds ?? bundle.metaExpectedPipeWelds,
    metaOngoingPipe: meta.ongoingPipe ?? bundle.metaOngoingPipe,
    metaLocation: meta.location ?? bundle.metaLocation,
    metaManager: meta.manager ?? bundle.metaManager,
    metaCrceRepresentative: meta.crceRepresentative ?? bundle.metaCrceRepresentative,
    showProjectStatusCell: meta.showProjectStatusCell ?? bundle.showProjectStatusCell,
    metaProjectStatus: meta.dprProjectStatus ?? bundle.metaProjectStatus,
    metaNdtType: meta.ndtType ?? bundle.metaNdtType,
    suppressPipelineProgress: meta.suppressPipelineProgress ?? bundle.suppressPipelineProgress,
    showOffshorePobShiftGrid: bundle.showOffshorePobShiftGrid ?? isOffshoreProject(ctx.project),
    clientTag: pick(bundle.clientTag, meta.client),
  };
}

function enrichManpowerFromProjectContext(manpower, { project } = {}) {
  if (!manpower || typeof manpower !== 'object') return manpower;
  const next = { ...manpower };
  if (isOffshoreProject(project) && next.showOffshorePobShiftGrid !== true) {
    next.showOffshorePobShiftGrid = true;
  }
  return next;
}

/** Offshore one-page: signature block uses Project Engineer from DPR log. */
const OFFSHORE_APPROVED_BY1_LABEL =
  'Prepared by the CRC Evans Project Engineer on behalf of the Production Superintendent';
const LEGACY_OFFSHORE_APPROVED_BY1_LABEL = 'CRCE Project Engineer';

function resolveOffshoreApprovedBy1Label(existingLabel) {
  const fromBe = existingLabel != null ? String(existingLabel).trim() : '';
  if (!fromBe || fromBe === LEGACY_OFFSHORE_APPROVED_BY1_LABEL) {
    return OFFSHORE_APPROVED_BY1_LABEL;
  }
  return fromBe;
}

function enrichOffshoreOnePageSignatures(signatures, { project, dprLog } = {}) {
  if (!isOffshoreProject(project) || !signatures || typeof signatures !== 'object') {
    return signatures;
  }
  const engineer = dprLog?.approvedBy1;
  const next = { ...signatures };
  if (next.approvedBy1 || engineer) {
    const existing = next.approvedBy1 || {};
    const label = resolveOffshoreApprovedBy1Label(existing.label);
    next.approvedBy1 = {
      ...existing,
      label,
      name: engineer?.teamMemberName || existing.name,
      position: resolveOffshoreApprovedBy1Label(existing.position) || label,
      signature: engineer?.signature || existing.signature,
    };
  }
  next.approvedBy3 = null;
  return next;
}

/** Normalize dashboard API payload into section objects the UI expects. */
export function normalizeOnePageDashboardPayload(raw, context) {
  const payload = unwrapOnePageApiPayload(raw);
  if (!payload) return null;

  const bundle = payload.bundle && typeof payload.bundle === 'object' ? payload.bundle : null;
  const builtMeta = buildMeta(payload, bundle);
  const meta = enrichMetaFromProjectContext(builtMeta, context);
  const signatures = enrichOffshoreOnePageSignatures(
    buildSignatures(payload, bundle),
    context
  );

  // Seed dashboard meta pipe fields + suppress onto bundle so HTML/PDF preview stay in sync.
  const bundleWithMetaPipe = {
    ...(bundle || {}),
    suppressPipelineProgress:
      builtMeta.suppressPipelineProgress || bundle?.suppressPipelineProgress || false,
    metaExpectedPipeWelds: pick(builtMeta.expectedPipeWelds, bundle?.metaExpectedPipeWelds),
    metaExpectedPipeLength: pick(builtMeta.expectedPipeLength, bundle?.metaExpectedPipeLength),
    metaOngoingPipe: pick(builtMeta.ongoingPipe, bundle?.metaOngoingPipe),
  };
  const enrichedBundle = context
    ? enrichBundleFromProjectContext(bundleWithMetaPipe, context)
    : applySuppressPipelineProgressToBundle(bundleWithMetaPipe);

  const bundleWithSignatures = signatures?.approvedBy1
    ? {
        ...enrichedBundle,
        approvedBy1Label: signatures.approvedBy1.label ?? enrichedBundle.approvedBy1Label,
        approvedBy1Position:
          signatures.approvedBy1.position ?? enrichedBundle.approvedBy1Position,
        approvedBy1Name: signatures.approvedBy1.name ?? enrichedBundle.approvedBy1Name,
        approvedBy1Signature:
          signatures.approvedBy1.signature ?? enrichedBundle.approvedBy1Signature,
      }
    : enrichedBundle;

  return {
    ...payload,
    bundle: bundleWithSignatures,
    header: buildHeader(payload, bundle),
    meta,
    progress: buildProgress(payload, bundle),
    quality: buildQuality(payload, bundle),
    safety: buildSafety(payload, bundle),
    downtime: buildDowntime(payload, bundle),
    manpower: enrichManpowerFromProjectContext(buildManpower(payload, bundle), context),
    comments: buildComments(payload, bundle),
    signatures,
    criticalConsumables: buildCriticalConsumables(payload, bundle),
  };
}

function applySuppressPipelineProgressToBundle(bundle) {
  if (!bundle || typeof bundle !== 'object' || !bundle.suppressPipelineProgress) return bundle || {};
  return {
    ...bundle,
    metaExpectedPipeWelds: '',
    metaExpectedPipeLength: '',
    metaOngoingPipe: '',
  };
}

/**
 * Fetch one-page DPR dashboard data (structured sections, matches PDF template).
 * GET /dpr-log/one-page-dashboard?dprLogId={id}&shift={shift}
 */
export async function fetchOnePageDashboard(
  dprLogId,
  {
    shift = 'both',
    project,
    pipeline,
    dprLog,
    allPipelines,
    sowId,
    ongoingPipelineId,
    dprProjectStatus,
    date,
  } = {}
) {
  if (!dprLogId) throw new Error('DPR log is not loaded yet.');
  const context = { project, pipeline, dprLog, allPipelines, date };
  const params = { dprLogId, shift };
  if (sowId != null && sowId !== '') params.sowId = sowId;
  if (ongoingPipelineId != null && ongoingPipelineId !== '') {
    params.ongoingPipelineId = ongoingPipelineId;
  }
  if (dprProjectStatus) params.dprProjectStatus = dprProjectStatus;
  const response = await axiosConfig.get('/dpr-log/one-page-dashboard', {
    params,
  });
  let normalized = normalizeOnePageDashboardPayload(response?.data ?? response, context);

  if (!hasDashboardSections(normalized)) {
    try {
      const viewPayload = await fetchOnePageReportViewRaw(dprLogId, { shift });
      const fromView = normalizeOnePageDashboardPayload(viewPayload, context);
      if (hasDashboardSections(fromView)) {
        normalized = fromView;
      }
    } catch (_) {
      /* keep primary dashboard payload */
    }
  }

  return normalized;
}

async function fetchOnePageReportViewRaw(dprLogId, { shift, sowId } = {}) {
  const params = {};
  if (shift) params.shift = shift;
  if (sowId != null && sowId !== '') params.sowId = sowId;

  const response = await axiosConfig.get(`/dpr-log/${dprLogId}/one-page-report-view`, {
    params,
  });
  return response?.data ?? response;
}

/**
 * Fetch structured one-page DPR view payload (mirrors backend PDF Handlebars bundle).
 * GET /dpr-log/{dprLogId}/one-page-report-view
 */
export async function fetchOnePageReportView(dprLogId, { shift, sowId } = {}) {
  if (!dprLogId) {
    throw new Error('DPR log is not loaded yet.');
  }
  const raw = await fetchOnePageReportViewRaw(dprLogId, { shift, sowId });
  return unwrapOnePageApiPayload(raw) ?? raw;
}

/** Normalize API shapes to { bundle, report, signatures, showSubContractor }. */
export function normalizeOnePageReportViewPayload(raw, context) {
  if (!raw || typeof raw !== 'object') {
    return { bundle: {}, report: null, signatures: null, showSubContractor: false };
  }

  let bundle = raw.bundle && typeof raw.bundle === 'object' ? raw.bundle : { ...raw };
  const dashboardSections = normalizeOnePageDashboardPayload(raw, context);
  if (dashboardSections?.criticalConsumables) {
    bundle = {
      ...bundle,
      criticalConsumables: dashboardSections.criticalConsumables,
    };
  }
  if (dashboardSections?.safety) {
    const safety = dashboardSections.safety;
    bundle = {
      ...bundle,
      safetyTbtTodayCount: pick(bundle.safetyTbtTodayCount, safety.tbtToday),
      safetyTbtToDate: pick(bundle.safetyTbtToDate, safety.tbtToDate),
      safetyTbtComments: normalizeToolboxTalkComments(
        pick(safety.tbtComments, bundle.safetyTbtComments)
      ),
    };
  }
  if (dashboardSections?.quality) {
    const quality = dashboardSections.quality;
    bundle = {
      ...bundle,
      // Prefer normalized quality (reject wording / PDF defaults) over raw bundle dashes.
      pipeDefectGaugeLabel: pick(quality.pipeDefectGaugeLabel, bundle.pipeDefectGaugeLabel),
      projectDefectGaugeLabel: pick(quality.projectDefectGaugeLabel, bundle.projectDefectGaugeLabel),
      pipeDefectGaugeCaption: pick(quality.pipeDefectGaugeCaption, bundle.pipeDefectGaugeCaption),
      projectDefectGaugeCaptionLine1: pick(
        quality.projectDefectGaugeCaptionLine1,
        bundle.projectDefectGaugeCaptionLine1
      ),
      projectDefectGaugeCaptionLine2: pick(
        quality.projectDefectGaugeCaptionLine2,
        bundle.projectDefectGaugeCaptionLine2
      ),
    };
  }
  if (context) {
    bundle = enrichBundleFromProjectContext(bundle, context);
  }
  const report = raw.report && typeof raw.report === 'object' ? raw.report : raw.reportDetails?.reports?.onePage ?? null;
  const signatures = raw.signatures && typeof raw.signatures === 'object' ? raw.signatures : null;

  const showSubContractor = Boolean(
    raw.showSubContractor
      ?? raw.signatures?.showSubContractor
      ?? bundle.showSubContractor
      ?? (signatures?.subContractor && (signatures.subContractor.name || signatures.subContractor.signatureUrl))
  );

  return { bundle, report, signatures, showSubContractor };
}

export function displayOnePageVal(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  const s = String(value).trim();
  // Backend often sends an em-dash placeholder; treat it as empty when a real fallback exists.
  if ((s === '—' || s === '-') && fallback !== '—') return fallback;
  return s;
}
